import React, { useCallback, useMemo, useState } from 'react';

import { AddIcon, Button, DeleteIcon, Text, dialogService } from '@thefarmersfront/kitchen';

import { Table } from '~/components/common';

import { getVenderSupplierRegisterManagerInfoColumnDefs } from '~/utils';

import { VenderSupplierManagerAddModal, VenderSupplierManagerModifyModal } from '..';

import type { TypeManagerRowInfo, TypeOwnerSupplierManager, TypeSupplierAddFormValue } from '~/types/vender.types';

import styles from './VenderSupplierAddModalManagerInfoSection.module.scss';

type TypeVenderSupplierAddModalManagerInfoSectionProps = {
  venderSupplierAddForm: TypeSupplierAddFormValue;
  setVenderSupplierAddForm: React.Dispatch<React.SetStateAction<TypeSupplierAddFormValue>>;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 등록 모달 컴포넌트 > 담당자 정보 영역
 */
const VenderSupplierAddModalManagerInfoSection = ({
  venderSupplierAddForm,
  setVenderSupplierAddForm,
}: TypeVenderSupplierAddModalManagerInfoSectionProps) => {
  const [selectedManagerInfo, setSelectedManagerInfo] = useState<TypeManagerRowInfo | null>(null);
  const [selectedManagerRowsList, setSelectedManagerRowsList] = useState<TypeOwnerSupplierManager[]>([]);

  const [isVenderSupplierManagerAddModalOpen, setIsVenderSupplierManagerAddModalOpen] = useState(false);
  const [isVenderSupplierManagerModifyModalOpen, setIsVenderSupplierManagerModifyModalOpen] = useState(false);

  const handleClickSupplierManagerModify = useCallback((data: TypeOwnerSupplierManager, rowIndex: number) => {
    setIsVenderSupplierManagerModifyModalOpen(true);
    setSelectedManagerInfo({ ...data, rowIndex });
  }, []);

  const { columnDefs } = useMemo(
    () => getVenderSupplierRegisterManagerInfoColumnDefs({ onClickViewDetail: handleClickSupplierManagerModify }),
    [handleClickSupplierManagerModify]
  );

  // 첫 행은 대표 담당자라 선택·삭제할 수 없다.
  const representativeManager = venderSupplierAddForm.managerInfoList[0];

  const handleClickSupplierManagerAdd = () => setIsVenderSupplierManagerAddModalOpen(true);

  const handleClickSupplierManagerDelete = () => {
    const deleteSelectedManagerRows = () => {
      const updatedManagerInfoList = venderSupplierAddForm.managerInfoList.filter(
        (manager) => !selectedManagerRowsList.includes(manager)
      );

      setVenderSupplierAddForm({
        ...venderSupplierAddForm,
        managerInfoList: updatedManagerInfoList,
      });
    };

    dialogService.addDialog({
      type: 'confirm',
      children: '담당자 정보를 정말 삭제하시겠습니까?',
      confirmText: '삭제',
      onConfirm: deleteSelectedManagerRows,
    });
  };

  const handleVenderSupplierManagerAddModalCancel = () => setIsVenderSupplierManagerAddModalOpen(false);

  const handleVenderSupplierManagerAddModalConfirm = (managerInfo: TypeOwnerSupplierManager) => {
    setVenderSupplierAddForm({
      ...venderSupplierAddForm,
      managerInfoList: [...venderSupplierAddForm.managerInfoList, managerInfo],
    });
    setIsVenderSupplierManagerAddModalOpen(false);
  };

  const handleVenderSupplierManagerModifyModalCancel = () => setIsVenderSupplierManagerModifyModalOpen(false);

  const handleVenderSupplierManagerModifyModalConfirm = (managerInfo: unknown) => {
    const { rowIndex, ...updatedManagerInfo } = managerInfo as TypeManagerRowInfo;

    const updatedManagerInfoList = venderSupplierAddForm.managerInfoList.map((manager, index) => {
      if (index !== rowIndex) {
        return manager;
      } else {
        return updatedManagerInfo;
      }
    });

    setVenderSupplierAddForm({
      ...venderSupplierAddForm,
      managerInfoList: updatedManagerInfoList,
    });
    setIsVenderSupplierManagerModifyModalOpen(false);
  };

  return (
    <>
      <div className={styles['manager-info-section']}>
        <div className={styles['table-title-wrapper']}>
          <div className={styles['table-title-side-field']}>
            <Text size="xxlarge" weight="bold" element="span">
              담당자 정보
            </Text>
            <Text weight="semi-bold" color="tertiary" element="span">
              {`${venderSupplierAddForm.managerInfoList.length}/5`}
            </Text>
          </div>
          {
            <div className={styles['table-title-side-field']}>
              <Button
                size="small"
                color="tertiary"
                variant="outlined"
                leftIcon={<DeleteIcon size={16} />}
                disabled={selectedManagerRowsList.length === 0}
                onClick={handleClickSupplierManagerDelete}
              >
                삭제
              </Button>
              <Button
                size="small"
                color="secondary"
                leftIcon={<AddIcon color="white" size={16} />}
                disabled={venderSupplierAddForm.managerInfoList.length >= 5}
                onClick={handleClickSupplierManagerAdd}
              >
                추가
              </Button>
            </div>
          }
        </div>
        <div className={styles['table-wrapper']}>
          <Table
            data={venderSupplierAddForm.managerInfoList}
            columns={columnDefs}
            rowStatusType="SEARCH_EMPTY"
            selection={{
              onChange: setSelectedManagerRowsList,
              isRowSelectable: (manager) => manager !== representativeManager,
              showDisabledCheckboxes: true,
              dimUnselectableRows: true,
            }}
          />
        </div>
      </div>
      {isVenderSupplierManagerAddModalOpen && (
        <VenderSupplierManagerAddModal
          open={isVenderSupplierManagerAddModalOpen}
          onCancel={handleVenderSupplierManagerAddModalCancel}
          onConfirm={handleVenderSupplierManagerAddModalConfirm}
        />
      )}
      {isVenderSupplierManagerModifyModalOpen && (
        <VenderSupplierManagerModifyModal
          open={isVenderSupplierManagerModifyModalOpen}
          managerInfo={selectedManagerInfo}
          onCancel={handleVenderSupplierManagerModifyModalCancel}
          onConfirm={handleVenderSupplierManagerModifyModalConfirm}
        />
      )}
    </>
  );
};

export default VenderSupplierAddModalManagerInfoSection;
